import { moduleActionContext } from "@/store/index";
import { AxiosError, AxiosResponse } from "axios";
import { defineModule } from "direct-vuex";
import ListItemNumberModel from "../../models/listItemNumberModel";
import { axiosInstance } from "../../plugins/axios";
import { SupervisorTransportDocumentTypeEnum } from "../../models/emums/supervisor/supervisorTransportDocumentTypeEnum";

export interface SupervisorTransportDocumentModuleStateInterface {
    adrTransportDocumentTypeList: ListItemNumberModel[];
    dgnTransportDocumentTypeList: ListItemNumberModel[];
    showAdrDialog: boolean;
    showDgnDialog: boolean;
    title: string;
    resolve: any;
    currentSupervisorLoadingPlaceId: undefined | number;
    currentSupervisorUnloadingPlaceId: undefined | number;
}

const supervisorTransportDocumentModule = defineModule({
    namespaced: true,
    state: {
        adrTransportDocumentTypeList: [] as ListItemNumberModel[],
        dgnTransportDocumentTypeList: [] as ListItemNumberModel[],
        showAdrDialog: false,
        showDgnDialog: false,
        title: "",
        resolve: null,
        currentSupervisorLoadingPlaceId: undefined,
        currentSupervisorUnloadingPlaceId: undefined,
        currentDocumentKind: "transport"
    } as SupervisorTransportDocumentModuleStateInterface,
    mutations: {
        setAdrTransportDocumentTypeList(state, value: ListItemNumberModel[]) {
            state.adrTransportDocumentTypeList = value;
        },
        setDgnTransportDocumentTypeList(state, value: ListItemNumberModel[]) {
            state.dgnTransportDocumentTypeList = value;
        },
        setShowAdrDialog(state, value: boolean) {
            state.showAdrDialog = value;
        },
        setShowDgnDialog(state, value: boolean) {
            state.showDgnDialog = value;
        },
        setTitle(state, value: string) {
            state.title = value;
        },
        setResolve(state, value: any) {
            state.resolve = value;
        },
        setCurrentSupervisorLoadingPlaceId(state, value: undefined | number) {
            state.currentSupervisorLoadingPlaceId = value;
        },
        setCurrentSupervisorUnloadingPlaceId(state, value: undefined | number) {
            state.currentSupervisorUnloadingPlaceId = value;
        },
    },
    actions: {
        loadAdrTransportDocumentTypeList(context) {
            const { commit } = _moduleActionContext(context)
            return new Promise((resolve) => {
                axiosInstance
                    .get("/api/supervisorTransportDocument/loadTransportDocumentTypeList/" + SupervisorTransportDocumentTypeEnum.ADR)
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data);
                        commit.setAdrTransportDocumentTypeList(resp.data)
                    })
            })
        },
        loadDgnTransportDocumentTypeList(context) {
            const { commit } = _moduleActionContext(context)
            return new Promise((resolve) => {
                axiosInstance
                    .get("/api/supervisorTransportDocument/loadTransportDocumentTypeList/" + SupervisorTransportDocumentTypeEnum.DGN)
                    .then((resp: AxiosResponse) => {
                        resolve(resp.data);
                        commit.setDgnTransportDocumentTypeList(resp.data)
                    })
            })
        },
        generateAndGetSupervisorTransporDocumentFile(context, payload: { transportDocumentTypeId: null | number }) {
            const { state } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .get("/api/supervisorTransportDocument/generateAndGetSupervisorTransporDocumentFile/" + payload.transportDocumentTypeId + "/" + state.currentSupervisorLoadingPlaceId + "/" + state.currentSupervisorUnloadingPlaceId, {
                        responseType: "blob"
                    })
                    .then((resp: AxiosResponse) => {
                        const blob = new Blob([resp.data], {
                            type: resp.headers["content-type"]
                        });
                        const contentDisposition = resp.headers["content-disposition"];
                        const filename = contentDisposition
                            .split(";")[1]
                            .split("=")[1]
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.style.display = "none";
                        document.body.appendChild(a);
                        a.download = filename;
                        a.href = url;
                        a.click();
                        window.URL.revokeObjectURL(url);

                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            });
        },
        generateAndGetSupervisorDgnFile(context, payload: { transportDocumentTypeId: null | number; netWeightKg: string }) {
            const { state } = _moduleActionContext(context)
            return new Promise((resolve, reject) => {
                axiosInstance
                    .get("/api/supervisorTransportDocument/generateAndGetSupervisorDgnFile/" + payload.transportDocumentTypeId + "/" + state.currentSupervisorLoadingPlaceId + "/" + state.currentSupervisorUnloadingPlaceId + "/" + encodeURIComponent(payload.netWeightKg), {
                        responseType: "blob"
                    })
                    .then((resp: AxiosResponse) => {
                        const blob = new Blob([resp.data], {
                            type: resp.headers["content-type"]
                        });
                        const contentDisposition = resp.headers["content-disposition"];
                        const filename = contentDisposition
                            .split(";")[1]
                            .split("=")[1]
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.style.display = "none";
                        document.body.appendChild(a);
                        a.download = filename;
                        a.href = url;
                        a.click();
                        window.URL.revokeObjectURL(url);

                        resolve(resp.data);
                    })
                    .catch((err: AxiosError) => reject(err));
            });
        },
        isConfirmedAdr(context) {
            const { state, commit } = _moduleActionContext(context);
            state.resolve(true);
            commit.setShowAdrDialog(false);
        },
        isConfirmedDgn(context) {
            const { state, commit } = _moduleActionContext(context);
            state.resolve(true);
            commit.setShowDgnDialog(false);
        },
        isNotConfirmedAdr(context) {
            const { commit } = _moduleActionContext(context);
            commit.setShowAdrDialog(false);
        },
        isNotConfirmedDgn(context) {
            const { commit } = _moduleActionContext(context);
            commit.setShowDgnDialog(false);
        },
        openAdrDialog(
            context,
            payload: {
                showAdrDialog: boolean;
                title: string;
            }
        ) {
            const { commit } = _moduleActionContext(context);
            commit.setShowAdrDialog(payload.showAdrDialog);
            commit.setTitle(payload.title);
            return new Promise(resolve => {
                commit.setResolve(resolve);
            });
        },
        openDgnDialog(
            context,
            payload: {
                showDgnDialog: boolean;
                title: string;
            }
        ) {
            const { commit } = _moduleActionContext(context);
            commit.setShowDgnDialog(payload.showDgnDialog);
            commit.setTitle(payload.title);
            return new Promise(resolve => {
                commit.setResolve(resolve);
            });
        },
    }
});

export default supervisorTransportDocumentModule;
const _moduleActionContext = (context: any) => moduleActionContext(context, supervisorTransportDocumentModule)